import type { CSSProperties } from 'react'

/** Base background style for the root body; config.background_image is merged in when set. */
export const BASE_BACKGROUND_STYLE: CSSProperties = {
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundAttachment: 'fixed',
    minHeight: '100dvh',
    margin: 0
}

/**
 * Build body style for the root layout.
 * Merges BASE_BACKGROUND_STYLE with config.background_image when present.
 */
export function getRootBodyStyle(config: IDomainConfig | null | undefined): CSSProperties {
    return {
        ...BASE_BACKGROUND_STYLE,
        ...(config?.background_image && { backgroundImage: `url(${config.background_image})` })
    }
}

/**
 * Inline script body to inject :root CSS variables before React hydrates (avoids FOUC).
 * Only used when theme has custom CSS variables; the caller decides whether to render the Script.
 */
export const THEME_INLINE_STYLE_ID = 'theme-inline-styles'

export function getThemeInlineScript(cssVariables: string): string {
    return `(function(){const el=document.getElementById('${THEME_INLINE_STYLE_ID}');if(el)el.remove();const style=document.createElement('style');style.id='${THEME_INLINE_STYLE_ID}';style.textContent=':root { ${cssVariables} }';document.head.appendChild(style);})();`
}

/** Remove the inline theme style element (used when switching from custom to preset theme) */
export function removeThemeInlineStyle(): void {
    if (typeof document === 'undefined') return
    document.getElementById(THEME_INLINE_STYLE_ID)?.remove()
}

/** Custom theme CSS vars – must be cleared when loading preset so [data-theme] rules apply */
const CUSTOM_THEME_VAR_NAMES = [
    '--background', '--muted-foreground', '--foreground', '--card-foreground', '--card',
    '--popover-foreground', '--primary-foreground', '--secondary-foreground', '--accent-foreground',
    '--destructive-foreground', '--warning-foreground', '--info-foreground', '--success-foreground',
    '--primary', '--secondary', '--muted', '--accent', '--popover', '--destructive',
    '--border', '--input', '--ring', '--info', '--success', '--warning'
]

/**
 * Inline script to clear any saved custom theme (inline style + CSS vars) and set data-theme.
 * Run beforeInteractive when loading a preset so stale custom CSS from a previous page doesn't override.
 */
export function getThemeInitScript(dataTheme: string): string {
    const clearVars = CUSTOM_THEME_VAR_NAMES.map(n => `d.style.removeProperty('${n}')`).join(';')
    return `(function(){var d=document.documentElement;var el=document.getElementById('${THEME_INLINE_STYLE_ID}');if(el)el.remove();${clearVars};d.setAttribute('data-theme','${dataTheme}');})();`
}
